import { Marquee } from "@/components/ui/marquee";
import { cn } from "@/lib/utils";
import { ClaudeIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
interface IconProps {
  className?: string;
}

function OpenAIIcon({ className }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={cn("size-5 sm:size-6", className)}
    >
      <path d="M22.28 9.82a5.98 5.98 0 0 0-.52-4.91 6.05 6.05 0 0 0-6.51-2.9A6.07 6.07 0 0 0 4.98 4.18a5.98 5.98 0 0 0-4 2.9 6.05 6.05 0 0 0 .74 7.1 5.98 5.98 0 0 0 .51 4.91 6.05 6.05 0 0 0 6.52 2.9A5.98 5.98 0 0 0 13.26 24a6.06 6.06 0 0 0 5.77-4.21 5.99 5.99 0 0 0 4-2.9 6.06 6.06 0 0 0-.75-7.07zm-9.02 12.61a4.48 4.48 0 0 1-2.88-1.04l.14-.08 4.78-2.76a.79.79 0 0 0 .39-.68v-6.74l2.02 1.17a.07.07 0 0 1 .04.05v5.58a4.5 4.5 0 0 1-4.49 4.5zm-9.66-4.13a4.47 4.47 0 0 1-.53-3.01l.14.09 4.78 2.76a.77.77 0 0 0 .78 0l5.84-3.37v2.33a.08.08 0 0 1-.03.06l-4.84 2.79a4.5 4.5 0 0 1-6.14-1.65zM2.34 7.9a4.49 4.49 0 0 1 2.37-1.97V11.6a.77.77 0 0 0 .39.68l5.81 3.35-2.02 1.17a.08.08 0 0 1-.07 0L4.02 14.02A4.5 4.5 0 0 1 2.34 7.87zm16.6 3.86L13.1 8.36l2.02-1.16a.08.08 0 0 1 .07 0l4.83 2.79a4.49 4.49 0 0 1-.68 8.1V12.44a.79.79 0 0 0-.4-.68zm2.01-3.02l-.14-.09-4.77-2.78a.78.78 0 0 0-.79 0L9.41 9.23V6.9a.07.07 0 0 1 .03-.06l4.83-2.79a4.5 4.5 0 0 1 6.68 4.66zM8.31 12.86l-2.02-1.16a.08.08 0 0 1-.04-.06V6.07a4.5 4.5 0 0 1 7.38-3.45l-.14.08-4.78 2.76a.79.79 0 0 0-.39.68zm1.1-2.37l2.6-1.5 2.6 1.5v3l-2.6 1.5-2.6-1.5z" />
    </svg>
  );
}

function GeminiIcon({ className }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={cn("size-5 sm:size-6", className)}
    >
      <path d="M12 0c0 6.627-5.373 12-12 12 6.627 0 12 5.373 12 12 0-6.627 5.373-12 12-12-6.627 0-12-5.373-12-12Z" />
    </svg>
  );
}

function ElevenLabsIcon({ className }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={cn("size-5 sm:size-6", className)}
    >
      <rect x="7" y="3" width="3.5" height="18" rx="1" />
      <rect x="13.5" y="3" width="3.5" height="18" rx="1" />
    </svg>
  );
}

function StabilityIcon({ className }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={cn("size-5 sm:size-6", className)}
    >
      <path d="M12 2L2 12l10 10 10-10L12 2zm0 3.41L19.59 12 12 19.59 4.41 12 12 5.41z" />
    </svg>
  );
}

interface Model {
  name: string;
  icon?: React.ComponentType<IconProps>;
}

function CustomClaudeIcon({ className }: IconProps) {
  return (
    <div className={cn("size-5 sm:size-6", className)}>
      <HugeiconsIcon icon={ClaudeIcon} className="w-full h-full" />
    </div>
  );
}

const models: Model[] = [
  { name: "GPT", icon: OpenAIIcon },
  { name: "Gemini", icon: GeminiIcon },
  { name: "Claude", icon: CustomClaudeIcon },
  { name: "Veo 3.1" },
  { name: "Sora" },
  { name: "Nano Banana 2" },
  { name: "Nano Banana Pro" },
  { name: "FLUX" },
  { name: "KlingAI" },
  { name: "DALL·E" },
  { name: "Runway" },
  { name: "ElevenLabs", icon: ElevenLabsIcon },
  { name: "MoonValley" },
  { name: "MiniMax" },
  { name: "Wan" },
  { name: "Pika" },
  { name: "Stable Diffusion", icon: StabilityIcon },
];

export function SupportedModels() {
  return (
    <section className="py-20 relative overflow-hidden">
      <div className="mx-auto max-w-6xl px-6 text-center">
        <h2 className="text-xs sm:text-sm font-medium text-muted-foreground tracking-widest uppercase mb-10">
          Powered by your favorite AI models
        </h2>

        <div className="relative flex w-full flex-col items-center justify-center overflow-hidden">
          <Marquee pauseOnHover className="[--duration:50s]">
            {models.map((model) => (
              <div
                key={model.name}
                className="mx-8 sm:mx-12 flex items-center gap-2.5 justify-center opacity-40 hover:opacity-100 transition-opacity duration-300"
              >
                {model.icon && <model.icon />}
                <span className="font-sans text-xl sm:text-2xl font-semibold tracking-tight text-foreground whitespace-nowrap">
                  {model.name}
                </span>
              </div>
            ))}
          </Marquee>

          {/* Fade edges */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-background dark:from-background" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-1/4 bg-gradient-to-l from-background dark:from-background" />
        </div>
      </div>
    </section>
  );
}
